<template>
  <div class="dashboard-page">
    <Panel size="small" hide-header>
      <template v-slot:content>
        <div class="d-flex justify-content-start mb-2">
          <BaseButtonLoader
            v-if="chargeReceipts.length > 0"
            size="sm"
            variant="secondary"
            @click="exportToCsv"
          >
            Export CSV
          </BaseButtonLoader>
        </div>
        <!--
          Scrolls inside its panel rather than widening the page, a desktop's included. Its columns
          are about 750px wide: more than the panel has on a phone, or beside the sidebar at 1024px
          and below.
        -->
        <b-table
          :fields="fields"
          :items="chargeReceipts"
          :per-page="perPage"
          :current-page="currentPage"
          data-cy="table-credit-card-charges"
          responsive
          thead-class="text-nowrap"
          tbody-tr-class="text-nowrap"
        >
          <template #cell(paymentDate)="row">
            {{ `${dateFormat(row.item.paymentDate, 'MMM DD, YYYY')}` }}
          </template>

          <template #cell(transactionId)="row">
            <div class="text-nowrap">
              <b-button 
                variant="outline-info"
                size="sm"
                @click="getDownloadLink(row.item.transactionId)"
              >
                Download
              </b-button>
            </div>
          </template>

          <template #cell(amount)="row">
            <div class="text-nowrap">
              {{ `${formatCurrency(row.item.amount)}` }}
            </div>
          </template>

          <template #cell(cardType)="row">
            <img
              :alt="row.item.cardType"
              :src="`/img/${row.item.cardType}.png`"
              width="40px"
            >
          </template>

          <template #cell(lastCreditCardDigits)="row">
            {{ row.item.lastCreditCardDigits }}
          </template>
        </b-table>
        <b-pagination
          v-if="chargeReceipts.length > perPage"
          v-model="currentPage"
          :total-rows="chargeReceipts.length"
          :per-page="perPage"
          align="center"
          size="sm"
        />
      </template>
    </Panel>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import formatCurrency from '@/utils/formatCurrency'
import dateFormatMixin from '@/mixins/dateFormatMixin'
import BaseButtonLoader from '@/components/BaseButtonLoader.vue'

export default {
  name: 'CreditCardCharges',
  components: {
    BaseButtonLoader
  },
  mixins: [dateFormatMixin],
  data() {
    return {
      currentPage: 1,
      perPage: 15,
      fields: [
        { key: 'paymentDate', label: 'Date' },
        { key: 'amount', label: 'Amount' },
        { key: 'transactionDescription', label: 'Description' },
        { key: 'cardType', label: 'Card Type' },
        { key: 'lastCreditCardDigits', label: 'Last 4 digits' },
        { key: 'cardName', label: 'Card Name' },
        { key: 'transactionId', label: 'Actions' }
      ]
    }
  },

  computed: {
    ...mapState('dashboard', [
      'chargeReceipts',
      'creditCardChargesFetched'
    ])
  },

  async created() {
    if (!this.creditCardChargesFetched) {
      await this.getCreditCardCharges()
    }
  },

  methods: {
    ...mapActions('dashboard', [
      'getCreditCardCharges',
      'generateInvoiceDownloadLink'
    ]),

    async getDownloadLink(transactionId) {
      const downloadURL = await this.generateInvoiceDownloadLink({ invoiceId: transactionId })
      window.open(downloadURL)
    },

    exportToCsv() {
      const headers = ['Date', 'Amount (USD)', 'Description', 'Card Type', 'Last 4 Digits', 'Card Name']
      const rows = this.chargeReceipts.map(receipt => [
        this.dateFormat(receipt.paymentDate, 'YYYY-MM-DD'),
        receipt.amount,
        receipt.transactionDescription,
        receipt.cardType,
        receipt.lastCreditCardDigits,
        receipt.cardName
      ])

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell ?? ''}"`).join(','))
        .join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `payment-receipts-${new Date().toISOString().split('T')[0]}.csv`
      link.click()
      URL.revokeObjectURL(link.href)
    },

    formatCurrency
  }
}
</script>

<style lang="scss" scoped></style>
